import type Handsontable from 'handsontable-v16';
import { BaseEditor } from 'handsontable-v16/editors';
import { registerCellType } from 'handsontable-v16/cellTypes';
import type { CellProperties } from 'handsontable-v16/settings';
import type { CellValue, RangeType } from 'handsontable-v16/common';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { errorToast } from '@/helpers';
import { DateUtils, type DurationValidationErrorReason, type DurationValidationResult } from '@/utils/Date.utils';
import { createTimepointErrorMessage } from '@/components/UI/SpreadSheet/editors/TimepointErrorHandler';
import { fromInVivoValidator, type InVivoValidator } from '@/support/hotValidator.util';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import ReactDOM, { type Root } from 'react-dom/client';
import { StrictMode } from 'react';
import {
  type TimepointAction,
  timepointComparator,
  TimepointComponent,
} from '@/components/UI/SpreadSheet/editors/TimepointComponent';
import styles from '@/components/UI/SpreadSheet/editors/TimepointEditor.module.scss';

const valueToArrayV16 = (value: unknown): Array<string> => {
  if (Array.isArray(value)) {
    return value.reduce<Array<string>>((acc, v) => {
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (_isNotEmpty(trimmed)) {
          acc.push(trimmed);
        }
      } else if (typeof v === 'number') {
        acc.push(`${v}`);
      }
      return acc;
    }, []);
  }
  if (typeof value === 'string') {
    return value.split(',').reduce<Array<string>>((acc, v) => {
      const trimmed = v.trim();
      if (_isNotEmpty(trimmed)) {
        acc.push(trimmed);
      }
      return acc;
    }, []);
  }
  return [];
};

const nextCellCoordinatesV16 = (
  row: number,
  column: number,
  keypress: { key: 'Enter' | 'Tab'; shiftKey: boolean }
): { row: number; column: number } => {
  switch (keypress.key) {
    case 'Enter':
      return { row: keypress.shiftKey ? row - 1 : row + 1, column };
    case 'Tab':
      return { row, column: keypress.shiftKey ? column - 1 : column + 1 };
    default:
      throw new InVivoError(`Unexpected key ${keypress.key}`, { slug: 'timepoint-editor-v16-next-cell' });
  }
};

const timepointRendererV16 = (
  _hotInstance: Handsontable.Core,
  td: HTMLTableCellElement,
  _row: number,
  _column: number,
  _prop: string | number,
  value: unknown,
  _cellProperties: CellProperties
): void => {
  const arrayValue = valueToArrayV16(value);
  td.innerHTML = '';

  if (_isNotEmpty(arrayValue)) {
    const chips = document.createElement('div');
    chips.classList.add(styles.Chips);

    arrayValue.forEach((timepointValue) => {
      try {
        const parsedValue = DateUtils.renderDuration(timepointValue, {
          format: 'short',
          zeros: true,
          defaultResponse: '',
        });
        if (_isNotEmpty(parsedValue)) {
          const chip = document.createElement('div');
          chip.classList.add(styles.Chip);
          const chipValue = document.createElement('div');
          chipValue.classList.add(styles.Value);
          chipValue.innerText = parsedValue;
          chip.appendChild(chipValue);
          chips.appendChild(chip);
        }
      } catch {
        // Skip unparseable values in renderer
      }
    });
    td.appendChild(chips);
  }
};

export const timepointInVivoValidatorV16: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;
  const allow_zero = columnSettings.allow_zero === true;
  const allow_negative = columnSettings.allow_negative === true;
  const arrayValue = valueToArrayV16(value);

  if (_isEmpty(arrayValue)) {
    if (required) {
      return { valid: false, message: 'This field is required' };
    }
    return { valid: true };
  }

  const formatValidationResults = arrayValue.map<{ value: string } & DurationValidationResult>((tpValue) => {
    if (typeof tpValue === 'string') {
      return { value: tpValue, ...DateUtils.validateDurationRange(tpValue, { allow_zero, allow_negative }) };
    }
    return { value: tpValue, valid: false, reason: 'error_format' };
  });

  type InvalidResult = { value: string; valid: false; reason: DurationValidationErrorReason };
  const isInvalid = (r: { value: string } & DurationValidationResult): r is InvalidResult => !r.valid;
  const rangeInvalidValues = formatValidationResults.filter(isInvalid);
  if (_isNotEmpty(rangeInvalidValues)) {
    const errorsByType = rangeInvalidValues.reduce<Partial<Record<DurationValidationErrorReason, Array<string>>>>(
      (acc, result) => ({ ...acc, [result.reason]: [...(acc[result.reason] ?? []), result.value] }),
      {}
    );
    const firstError = (Object.entries(errorsByType) as Array<[DurationValidationErrorReason, Array<string>]>).sort(
      (left, right) => right[1].length - left[1].length
    )?.[0];
    if (_notNil(firstError)) {
      return {
        valid: false,
        message: createTimepointErrorMessage(firstError[0], { values: firstError[1], originalValues: firstError[1] }),
      };
    }
  }
  return { valid: true };
};

export const timepointAfterPasteHandlerV16 = function (
  this: Handsontable.Core,
  data: Array<Array<CellValue>>,
  coords: Array<RangeType>
): void {
  if (_isEmpty(coords) || _isEmpty(data)) {
    return;
  }

  try {
    const dataRows = data.length;
    const dataCols = data[0]?.length ?? 0;

    const immediateUpdates: Array<[number, number, CellValue]> = [];
    const asyncProcessingCells: Array<{
      row: number;
      col: number;
      validDurations: Array<string>;
      invalidValues: Array<string>;
    }> = [];

    for (const { startRow, startCol, endRow, endCol } of coords) {
      for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const cellMeta = this.getCellMeta(row, col);
          if (cellMeta.type === TimepointEditorV16.type || cellMeta.editor === TimepointEditorV16) {
            const dataRowIndex = (row - startRow) % dataRows;
            const dataColIndex = (col - startCol) % dataCols;
            const pasteValue = data[dataRowIndex]?.[dataColIndex];

            if (_isNil(pasteValue)) {
              continue;
            }

            const processedValues = valueToArrayV16(pasteValue);
            const allow_zero = cellMeta.allow_zero === true;
            const allow_negative = cellMeta.allow_negative === true;

            if (_isEmpty(processedValues)) {
              continue;
            }

            const { validDurations, invalidValues, rangeErrorsByType } = processedValues.reduce<{
              validDurations: Array<string>;
              invalidValues: Array<string>;
              rangeErrorsByType: Partial<Record<DurationValidationErrorReason, Array<string>>>;
            }>(
              (acc, pasteVal) => {
                const validation = DateUtils.validateDurationRange(pasteVal, { allow_zero, allow_negative });

                if (validation.valid) {
                  acc.validDurations.push(pasteVal);
                } else if (validation.reason === 'error_format') {
                  acc.invalidValues.push(pasteVal);
                } else {
                  acc.rangeErrorsByType[validation.reason] = [
                    ...(acc.rangeErrorsByType[validation.reason] ?? []),
                    pasteVal,
                  ];
                }
                return acc;
              },
              { validDurations: [], invalidValues: [], rangeErrorsByType: {} }
            );

            const rangeErrorsByTypeEntries = Object.entries(rangeErrorsByType) as Array<
              [DurationValidationErrorReason, Array<string>]
            >;
            if (rangeErrorsByTypeEntries.length > 0) {
              rangeErrorsByTypeEntries.forEach(([errorType, values]) => {
                errorToast(
                  createTimepointErrorMessage(errorType, {
                    values: values,
                    originalValues: values,
                  })
                );
              });
            }

            if (_isNotEmpty(invalidValues)) {
              asyncProcessingCells.push({ row, col, validDurations, invalidValues });
            } else if (_isNotEmpty(validDurations)) {
              immediateUpdates.push([row, col, validDurations.sort(timepointComparator)]);
            }
          }
        }
      }
    }

    if (_isNotEmpty(immediateUpdates)) {
      this.setDataAtCell(immediateUpdates, 'edit');
    }

    if (_isNotEmpty(asyncProcessingCells)) {
      asyncProcessingCells.forEach(({ row, col, validDurations, invalidValues }) => {
        Promise.allSettled(invalidValues.map((duration) => DateUtils.parseDuration(duration)))
          .then((results) => {
            const cellMeta = this.getCellMeta(row, col);
            const allow_zero = cellMeta.allow_zero === true;
            const allow_negative = cellMeta.allow_negative === true;
            const convertedValues: Array<string> = [];
            const failedValues: Array<string> = [];
            const rangeErrorsByType: Partial<
              Record<DurationValidationErrorReason, { parsedValues: Array<string>; originalValues: Array<string> }>
            > = {};

            for (let i = 0; i < results.length; i++) {
              const result = results[i];
              const originalValue = invalidValues[i];

              if (result.status === 'fulfilled') {
                const rangeValidation = DateUtils.validateDurationRange(result.value, { allow_zero, allow_negative });
                if (rangeValidation.valid) {
                  convertedValues.push(result.value);
                } else {
                  const errorKey = rangeValidation.reason;
                  rangeErrorsByType[errorKey] = {
                    parsedValues: [...(rangeErrorsByType[errorKey]?.parsedValues ?? []), result.value],
                    originalValues: [...(rangeErrorsByType[errorKey]?.originalValues ?? []), originalValue],
                  };
                }
              } else {
                failedValues.push(originalValue);
              }
            }

            if (_isNotEmpty(failedValues)) {
              errorToast(createTimepointErrorMessage('error_format', { values: failedValues }));
            }

            Object.entries(rangeErrorsByType).forEach(([errorType, errorData]) => {
              errorToast(
                createTimepointErrorMessage(errorType as DurationValidationErrorReason, {
                  values: errorData.parsedValues,
                  originalValues: errorData.originalValues,
                })
              );
            });

            const finalValue = _isNotEmpty(convertedValues)
              ? [...new Set([...validDurations, ...convertedValues])].sort(timepointComparator)
              : validDurations;

            if (_isNotEmpty(finalValue)) {
              this.setDataAtCell(row, col, finalValue, 'edit');
            } else {
              this.setDataAtCell(row, col, [], 'edit');
            }
          })
          .catch((error) => {
            if (_isNotEmpty(validDurations)) {
              this.setDataAtCell(row, col, validDurations, 'edit');
            } else {
              this.setDataAtCell(row, col, [], 'edit');
            }
            ExceptionHandler.captureException(
              new InVivoError('Error during timepoint async processing', {
                slug: 'timepoint-paste-async-error-v16',
                cause: error,
                level: 'warning',
              })
            );
          });
      });
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Error in timepoint paste handler', {
        slug: 'timepoint-paste-handler-error-v16',
        cause: error,
        level: 'error',
      })
    );
  }
};

interface TimepointCellPropertiesV16 extends CellProperties {
  singleValue?: boolean;
  allow_zero?: boolean;
  allow_negative?: boolean;
  required?: boolean;
}

export class TimepointEditorV16 extends BaseEditor {
  static type = 'timepoint-v16';

  static register(type = TimepointEditorV16.type): void {
    registerCellType(type, {
      editor: TimepointEditorV16 as unknown as typeof BaseEditor,
      renderer: timepointRendererV16,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validator: fromInVivoValidator(timepointInVivoValidatorV16) as any,
    });
  }

  displayed = false;
  timepointContainer: HTMLDivElement | null = null;
  timepointOverlay: HTMLDivElement | null = null;
  private reactDomRoot: Root | null = null;
  currentValue: Array<string> = [];
  declare originalValue: Array<string>;

  init(): void {
    this.hot.addHook('afterDestroy', () => this.destroyElements());
  }

  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: unknown,
    cellProperties: TimepointCellPropertiesV16
  ): void {
    const arrayValue = valueToArrayV16(originalValue);
    super.prepare(row, col, prop, td, arrayValue, cellProperties);
    this.originalValue = [...arrayValue];
    this.currentValue = [...arrayValue];
  }

  beginEditing(initialValue?: unknown, event?: Event): void {
    if (_notNil(initialValue)) {
      this.setValue(initialValue);
    }
    super.beginEditing(initialValue, event);
    this.open();
  }

  createElements(): void {
    this.timepointContainer = this.hot.rootDocument.createElement('div');
    this.timepointContainer.classList.add(styles.TimepointContainer);
    this.timepointContainer.setAttribute('data-test-component', 'TimepointEditorV16');
    this.timepointContainer.setAttribute('data-test-element', 'container');
    this.timepointContainer.style.display = 'none';

    this.timepointOverlay = this.hot.rootDocument.createElement('div');
    this.timepointOverlay.classList.add(styles.TimepointOverlay);
    this.timepointOverlay.setAttribute('data-test-element', 'overlay');
    this.timepointContainer.appendChild(this.timepointOverlay);
    this.hot.rootDocument.body.appendChild(this.timepointContainer);

    const handleMouseDown = (event: MouseEvent) => {
      if (_notNil(this.timepointContainer) && event.target === this.timepointContainer) {
        this.finishEditing(false, false, () => {
          this.hot.selectCell(this.row, this.col);
        });
      } else {
        event.stopImmediatePropagation();
      }
    };

    this.timepointContainer.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
  }

  destroyElements(): void {
    if (_notNil(this.timepointContainer)) {
      this.reactDomRoot?.unmount();
      this.reactDomRoot = null;
      this.timepointContainer.innerHTML = '';
      this.timepointContainer.remove();
      this.timepointContainer = null;
    }
  }

  getValue(): Array<string> {
    return this.currentValue;
  }

  setValue(newValue: unknown): void {
    this.currentValue = valueToArrayV16(newValue);

    if (this.isOpened() && _notNil(this.reactDomRoot)) {
      window.requestAnimationFrame(() => {
        this.render();
      });
    }
  }

  open(): void {
    this.display();
  }

  close(): void {
    this.display(false);
  }

  focus(): void {
    // Focus is handled by the React TimepointComponent
  }

  isOpened(): boolean {
    return this.displayed;
  }

  saveValue(): void {
    this.hot.setDataAtCell(this.row, this.col, this.currentValue, 'edit');
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false, callback?: () => void): void {
    if (this.isOpened()) {
      if (restoreOriginalValue && _notNil(this.originalValue)) {
        this.setValue(this.originalValue);
      } else {
        this.saveValue();
      }
    }

    super.finishEditing(restoreOriginalValue, ctrlDown, callback);
  }

  cancelChanges(): void {
    this.currentValue = [...this.originalValue];
    super.cancelChanges();
  }

  discardEditor(): void {
    this.display(false);
  }

  display(display = true): void {
    this.displayed = display;

    if (_isNil(this.timepointContainer) || _isNil(this.timepointOverlay)) {
      this.createElements();
    }

    if (display) {
      const offset = this.TD.getBoundingClientRect();
      (this.timepointOverlay as HTMLDivElement).style.top = `${offset.top}px`;
      (this.timepointOverlay as HTMLDivElement).style.left = `${offset.left}px`;
      (this.timepointOverlay as HTMLDivElement).style.width = `${offset.width}px`;
      (this.timepointContainer as HTMLDivElement).style.display = 'block';
      this.render();
    } else {
      (this.timepointContainer as HTMLDivElement).style.display = 'none';
      window.requestAnimationFrame(() => {
        this.reactDomRoot?.unmount();
        this.reactDomRoot = null;
        if (_notNil(this.timepointOverlay)) {
          this.timepointOverlay.innerHTML = '';
        }
      });
    }
  }

  actionHandler(action: TimepointAction): void {
    const currentRow = this.row;
    const currentCol = this.col;

    switch (action.type) {
      case 'update': {
        this.setValue(action.value);
        if (action.close) {
          this.close();
          this.hot.setDataAtCell(currentRow, currentCol, this.currentValue, 'edit');
          if (_notNil(action.keypress)) {
            const { row: nextRow, column: nextColumn } = nextCellCoordinatesV16(
              currentRow,
              currentCol,
              action.keypress
            );
            const clampedRow = Math.max(0, Math.min(nextRow, this.hot.countRows() - 1));
            const clampedCol = Math.max(0, Math.min(nextColumn, this.hot.countCols() - 1));
            setTimeout(() => {
              this.hot.selectCell(clampedRow, clampedCol);
            }, 10);
          } else {
            setTimeout(() => {
              this.hot.selectCell(currentRow, currentCol);
            }, 10);
          }
        }
        break;
      }
      case 'close': {
        this.close();
        this.hot.setDataAtCell(currentRow, currentCol, this.currentValue, 'edit');
        setTimeout(() => {
          this.hot.selectCell(currentRow, currentCol);
        }, 10);
        break;
      }
    }
  }

  render(): void {
    if (
      this.isOpened() &&
      _notNil(this.cellProperties) &&
      _notNil(this.timepointContainer) &&
      _notNil(this.timepointOverlay) &&
      this.timepointContainer?.style?.display === 'block'
    ) {
      if (_isNil(this.reactDomRoot)) {
        this.reactDomRoot = ReactDOM.createRoot(this.timepointOverlay);
      }

      const cellProps = this.cellProperties as TimepointCellPropertiesV16;

      this.reactDomRoot.render(
        <StrictMode>
          <TimepointComponent
            timepoints={this.currentValue}
            handleAction={(action) => this.actionHandler(action)}
            isSingleValueOnly={cellProps.singleValue === true}
          />
        </StrictMode>
      );
    }
  }
}
